import { Link, useMatch } from 'react-router';
import { t } from '@transifex/native';
import { Button, buttonVariants } from '~/components/ui/button';
import { useUser } from '~/hooks/user';
import { ampli } from '~/third-party/ampli/.client';
import { cn } from '~/lib/utils';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { NavbarUserMenu } from '~/components/ui/navbar-user-menu';
import { NavbarLanguageMenu } from '~/components/ui/navbar-language-menu';
import { UserMenuAvatar } from '~/components/ui/user-menu-avatar';

// Give transition effect with dynamic height
function DrilldownMenuContainer({
  activeIndex,
  panels,
}: {
  activeIndex: number;
  panels: ReactNode[];
}) {
  const [height, setHeight] = useState<number | undefined>(undefined);
  const panelRefs = useRef<(HTMLDivElement | null)[]>([]);

  useLayoutEffect(() => {
    const activePanel = panelRefs.current[activeIndex];
    if (activePanel) {
      setHeight(activePanel.scrollHeight);
    }
  }, [activeIndex]);

  return (
    <div
      className="overflow-hidden transition-[height] duration-200 ease-in-out"
      style={{ height }}
    >
      <div
        className="
          flex items-start transition-transform duration-200 ease-in-out
        "
        style={{ transform: `translateX(-${activeIndex * 100}%)` }}
      >
        {panels.map((panel, i) => (
          <div
            key={i}
            ref={el => {
              panelRefs.current[i] = el;
            }}
            className="w-full shrink-0"
          >
            {panel}
          </div>
        ))}
      </div>
    </div>
  );
}

export function NavbarMenu() {
  const user = useUser();
  const isSignupPage = useMatch('/signup');
  const isLoginPage = useMatch('/login');
  const isVerifyPage = useMatch('/signup/verify');
  const [view, setView] = useState<'user' | 'language'>('user');

  if (isSignupPage || isLoginPage || isVerifyPage) {
    return null;
  }

  if (!user) {
    return (
      <>
        <Link
          to="?auth=login"
          onClick={() => ampli.clickLogInTopNavigation()}
          className={cn(buttonVariants({ variant: 'default' }), 'w-24')}
        >
          Log in
        </Link>
        <Link
          to="?auth=signup"
          onClick={() => ampli.clickSignUpTopNavigation()}
          className={cn(buttonVariants({ variant: 'secondary' }), 'w-24')}
        >
          Sign up
        </Link>
      </>
    );
  }

  const xpLevel = user.gamificationProfile?.currentTier ?? null;

  return (
    <DropdownMenu
      onOpenChange={open => {
        if (open) {
          ampli.clickAvatarTopNavigation();
        } else {
          // Reset when menu closes
          setView('user');
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-12 overflow-visible rounded-md p-0"
          aria-label={t('User')}
        >
          <UserMenuAvatar user={user} xpLevel={xpLevel} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 overflow-hidden">
        <DrilldownMenuContainer
          activeIndex={view === 'language' ? 1 : 0}
          panels={[
            <NavbarUserMenu key="user" onSwitchToLanguage={() => setView('language')} />,
            <NavbarLanguageMenu key="language" onBack={() => setView('user')} />,
          ]}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
